import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Store, MapPin, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Shared card that renders the /api/ai/search result. Used by both the
// homepage hero AI-search and the standalone /ai-search page.
export const AiSearchResultCard = ({ result, onPostAsRequirement }) => {
  if (!result) return null;
  const { friendly_answer, matched_category, matched_subcategory, top_vendors, keywords, city, quantity_hint } = result;
  const target = matched_subcategory || matched_category;
  const targetPath = matched_subcategory
    ? `/sub-category/${matched_subcategory.slug}`
    : matched_category
      ? `/category/${matched_category.slug}`
      : null;
  return (
    <div className="mt-4 rounded-xl border border-orange-200 dark:border-orange-900/40 bg-gradient-to-br from-orange-50 to-white dark:from-[#1f120b] dark:to-[#111827] shadow-sm p-4 space-y-4" data-testid="ai-search-result">
      {friendly_answer && (
        <div className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
          <Sparkles className="w-4 h-4 mt-0.5 text-[#ee5e23] shrink-0" />
          <span>{friendly_answer}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-2 text-[11px]">
        {keywords && (
          <span className="px-2 py-0.5 rounded-full bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700">
            <span className="text-gray-500">keywords:</span> <b>{keywords}</b>
          </span>
        )}
        {city && (
          <span className="px-2 py-0.5 rounded-full bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {city}
          </span>
        )}
        {quantity_hint && (
          <span className="px-2 py-0.5 rounded-full bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700">
            qty: <b>{quantity_hint}</b>
          </span>
        )}
      </div>

      {targetPath && (
        <Link
          to={targetPath}
          className="inline-flex items-center gap-2 px-3 py-2 bg-[#2c54b2] hover:bg-[#243f8a] text-white text-sm rounded-md transition-colors"
          data-testid="ai-search-category-link"
        >
          Browse {target?.name} <ArrowRight className="w-4 h-4" />
        </Link>
      )}

      {top_vendors?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Top vendors matched ({top_vendors.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {top_vendors.slice(0, 4).map((v) => (
              <Link
                key={v.id}
                to={`/vendor/${v.slug || v.id}`}
                className="flex items-center gap-3 p-2 rounded-md bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 hover:border-[#ee5e23] hover:shadow-sm transition-all"
                data-testid={`ai-search-vendor-${v.id}`}
              >
                <div className="w-9 h-9 rounded bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {v.logo && v.logo.length <= 3 ? v.logo : <Store className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate text-gray-900 dark:text-gray-100 flex items-center gap-1">
                    {v.name}
                    {v.claimed && (
                      <span className="text-[9px] uppercase font-semibold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1 py-0.5">✓</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-2">
                    {v.city && <span><MapPin className="w-2.5 h-2.5 inline" /> {v.city}</span>}
                    {v.rating > 0 && <span><Star className="w-2.5 h-2.5 inline fill-yellow-400 text-yellow-400" /> {v.rating}</span>}
                    {v.plan_name && <span className="text-[#ee5e23]">{v.plan_name}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {(!top_vendors?.length && !matched_category && !matched_subcategory) && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          No exact match yet — try posting this as a requirement so vendors can respond directly.
        </div>
      )}

      {onPostAsRequirement && (
        <button
          type="button"
          onClick={() => onPostAsRequirement(result)}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#ee5e23] hover:bg-[#d24e15] text-white rounded-md text-sm font-medium transition-colors"
          data-testid="ai-search-post-requirement"
        >
          Post this as a Requirement <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Shared helper hook for running the /api/ai/search request with debounce.
export const useAiSearch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const run = useCallback(async (query, city = '') => {
    if (!query || query.trim().length < 2) return;
    setLoading(true); setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/ai/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), city: city || '' }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setResult(data);
    } catch (e) {
      setError(String(e?.message || e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => { setResult(null); setError(null); }, []);

  return { loading, error, result, run, reset };
};
